/**
 * Dialog View (Landscape/Desktop) - Custom centered dialog
 *
 * A centered dialog that appears in landscape mode with:
 * - Fade-in overlay animation
 * - Bounce-in dialog animation
 * - Scroll prevention on non-scrollable areas
 * - ESC key handling (managed by parent)
 */

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { SheetSurfaceProvider } from '@/presentation/contexts/SheetSurfaceContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { CTAButtons } from './CTAButtons';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { SheetState } from '../config/ExpauseSheet.types';

interface DialogViewProps {
  state: SheetState;
  onClose: () => void;
  zIndex: number;
  canClose: boolean;
}

export const DialogView: React.FC<DialogViewProps> = ({ state, onClose, zIndex, canClose }) => {
  const { theme } = useTheme();
  const responsiveDesign = useResponsiveDesign();
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  // Trigger animation on mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure the component is mounted and initial render is complete
    const raf = requestAnimationFrame(() => {
      setIsAnimatingIn(true);
    });
    return () => {
      cancelAnimationFrame(raf);
    };
  }, []);

  // Inject keyframes for the dialog animation
  useEffect(() => {
    const styleId = 'dialog-bounce-animation';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes dialogBounceIn {
          0% {
            transform: translate(-50%, -50%) scale(0.7);
            opacity: 0;
          }
          60% {
            transform: translate(-50%, -50%) scale(1.02);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (canClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Prevent scroll propagation to body
  // Body has overflow:hidden, but scroll events can still propagate when:
  // 1. Scrolling on non-scrollable areas (overlay, title, buttons)
  // 2. Scrolling on scrollable content when at boundaries
  // When scrollable is false, skip content ref so nested scroll containers can work
  const scrollPreventionContentRef = state.scrollable === false
    ? { current: null } as React.RefObject<HTMLDivElement>
    : contentRef;
  useScrollPrevention(scrollPreventionContentRef, [overlayRef, titleRef, buttonsRef], { preventTouch: true });

  const content = (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        {...qaAttr({ id: 'sheet-backdrop' })}
        className={`${state.container ? 'absolute' : 'fixed'} inset-0`}
        style={{
          backgroundColor: theme.modalOverlay,
          zIndex,
          pointerEvents: 'auto',
          userSelect: 'none', // Prevent text selection on overlay
          opacity: isAnimatingIn ? 1 : 0,
          transition: 'opacity 0.2s ease-out',
        }}
        onClick={handleOverlayClick}
        data-sheet-overlay={state.id}
        data-has-container={!!state.container}
      />

      {/* Dialog Content */}
      <div
        {...qaAttr({ id: state.qaId ? `sheet-${state.qaId}` : 'sheet-dialog', status: 'open' })}
        className={`flex flex-col ${state.container ? 'absolute' : 'fixed'} outline-none`}
        style={{
          backgroundColor: theme.modalBackground,
          borderRadius: responsiveDesign.dialog.borderRadius,
          width: state.width || responsiveDesign.dialog.width,
          // Ensure dialog doesn't shrink below responsive width, but never exceed maxWidth
          // (CSS: minWidth can beat maxWidth on small screens, so use min() to keep them consistent)
          minWidth: `min(${responsiveDesign.dialog.width}, ${state.maxWidth || responsiveDesign.dialog.maxWidth})`,
          maxWidth: state.maxWidth || responsiveDesign.dialog.maxWidth,
          ...(state.fixedHeight
            ? { height: state.fixedHeight, maxHeight: state.fixedHeight }
            : { maxHeight: state.maxHeight || responsiveDesign.dialog.maxHeight }),
          left: '50%',
          top: '50%',
          zIndex: zIndex + 1,
          pointerEvents: 'auto',
          ...(isAnimatingIn
            ? {
              animation: 'dialogBounceIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            }
            : {
              transform: 'translate(-50%, -50%) scale(0.95)',
              opacity: 0,
            }),
        }}
        data-sheet-content={state.id}
        data-sheet-title={state.title}
        data-has-container={!!state.container}
      >
        {/* Title */}
        {state.title && (
          <div
            ref={titleRef}
            className="font-semibold border-b flex-shrink-0"
            style={{
              paddingLeft: responsiveDesign.spacing['2xl'],
              paddingRight: responsiveDesign.spacing['2xl'],
              paddingTop: responsiveDesign.spacing.lg,
              paddingBottom: responsiveDesign.spacing.lg,
              fontSize: responsiveDesign.font.lg,
              color: theme.text.primary,
              borderBottomColor: theme.transparent, // clean look for dialog
            }}
          >
            {state.title}
          </div>
        )}

        {/* Content */}
        <div
          ref={contentRef}
          className={`flex-1 ${state.scrollable !== false ? 'overflow-y-auto scrollbar-hide' : ''}`}
          style={{
            color: theme.text.primary,
            userSelect: 'text', // Explicitly enable text selection in sheet content
            minHeight: 0, // Allow flex item to shrink for nested scroll to work
            // When scrollable is false, use overflow:hidden to constrain content
            // so nested scroll containers can work properly
            ...(state.scrollable === false && {
              overflow: 'hidden',
            }),
          }}
        >
          <SheetSurfaceProvider>{state.content}</SheetSurfaceProvider>
        </div>

        {/* CTA Buttons */}
        {state.cta && (
          <div ref={buttonsRef} className="flex-shrink-0">
            <CTAButtons cta={state.cta} onClose={onClose} />
          </div>
        )}
      </div>
    </>
  );

  // Render into container or body
  if (state.container) {
    return createPortal(content, state.container);
  }
  return createPortal(content, document.body);
};
